/**
 * Closed reason codes for one residential fee day (WS3 §2.3, item 36).
 *
 * Stored as text. The UI maps each code to plain language and must not render
 * the token. Codes carry no resident detail.
 *
 * Leave ordinals are numeric fields on the day result (`socialLeaveOrdinalFy`,
 * `hospitalEpisodeDay`). The codes stay finite so a label map can cover every
 * member. `LEAVE_SOCIAL` / `LEAVE_HOSPITAL` are the spec's `LEAVE_*_N` codes.
 *
 * Clause notes:
 * docs/operations/clause-verifications/2026-10-14-aged-care-legislation-doh-rac-care-fee-formula-primary-source-verification.md
 */

export const RESIDENTIAL_FEE_REASON_CODES = [
  'RATE_PERIOD_APPLIED',
  /** No rate row for this billed day. The day holds. Amounts stay zero. */
  'RATE_PERIOD_MISSING',
  /** A row was supplied, but its effective window does not cover the billed day. */
  'RATE_PERIOD_MISMATCH',
  'DAY_ENTRY',
  'DAY_DEPARTURE',
  'DAY_RETURN_NOT_LEAVE',
  'LEAVE_SOCIAL',
  'LEAVE_HOSPITAL',
  'SOCIAL_LEAVE_LIMIT_BED_RESERVATION',
  'BED_RESERVATION_FEE_AGREED',
  'BED_RESERVATION_NO_AGREEMENT',
  /** Transitional bed reservation needs the individual's as-if base rate. Missing evidence holds. */
  'BED_RESERVATION_BASE_RATE_MISSING',
  'MEANS_NOT_DISCLOSED',
  'MEANS_PENDING_ADVICE',
  /** D8: blank provider_informed_on used source_issued_at. */
  'PROVIDER_INFORMED_ON_ASSUMED_FROM_SOURCE_ISSUED_AT',
  'HOTELLING_BELOW_MAX',
  'HOTELLING_ZERO_DMTA_BELOW_MAS',
  'HOTELLING_NOT_APPLICABLE_TRANSITIONAL',
  'NCCC_ZERO_DMTA_BELOW_THRESHOLD',
  'NCCC_DAILY_MAX',
  'NCCC_1460_DAY_LIMIT',
  'NCCC_LIFETIME_CAP_EXCEEDED',
  'NCCC_LIFETIME_CAP_CROSSED_TODAY',
  'MTCF_COST_OF_CARE_CAP',
  'MTCF_UNDER_ONE_DOLLAR',
  'MTCF_ANNUAL_CAP_EXCEEDED',
  /** The single r 285A-14(2) period has ended. The annual cap does not apply and does not reset. */
  'MTCF_ANNUAL_CAP_PERIOD_ENDED',
  /** 29 February first entry has no anniversary inside the statutory window. Do not guess one. */
  'MTCF_ANNUAL_PERIOD_UNRESOLVABLE',
  /** Rules r 285A-14(7) cannot be evaluated because the applicable day or the MTCF informed date is missing. */
  'MTCF_LATE_INFORMATION_UNRESOLVABLE',
  'MTCF_LIFETIME_CAP_EXCEEDED',
  'ZERO_CLASS_DEPENDENT_CHILD',
  'ZERO_CLASS_FORMER_POW',
  'ZERO_CLASS_CONTINUING_HCP',
  'ZERO_CLASS_PRE2014_OPT_OUT',
  'ZERO_CLASS_INFORMED_AFTER_6_MONTHS',
  'ZERO_CLASS_DIED_BEFORE_INFORMED',
  'ZERO_CLASS_CEASED_BEFORE_INFORMED',
  'ZERO_DETERMINATION_S280',
  'FEE_REDUCTION_APPLIED',
  'FEE_REDUCTION_UNAPPLIED_REMAINDER',
  'ADVICE_DERIVATION_MISMATCH',
  'DEPARTURE_MEANS_TESTED_NOT_CHARGED_POLICY',
  /** C5 is out of this slice. The calculator does not price pre-1 July 2014. */
  'UNSUPPORTED_PRE_2014',
  /**
   * Billing policy. The means-tested care cohort has no first-entry day.
   * Appended so existing reason-code order in snapshots stays stable.
   */
  'MTCF_FIRST_ENTRY_DAY_MISSING',
  /** Advice recorded mtcf_zero_class. The subclass columns are not on the advice row. */
  'MTCF_ZERO_CLASS',
  /**
   * D7 (Act s 298, Rules r 298-10). Appended so existing order stays stable.
   * The accommodation component is a low-means daily accommodation contribution.
   */
  'ACCOMMODATION_LOW_MEANS_CONTRIBUTION',
  /** An agreed contribution above the advised maximum is charged at the maximum. */
  'ACCOMMODATION_CONTRIBUTION_CAPPED_AT_ADVISED_MAX',
  /** Fully supported resident: no accommodation amount is charged. */
  'ACCOMMODATION_FULLY_SUPPORTED',
  /** Low-means class without an advised maximum. The day holds. */
  'ACCOMMODATION_CONTRIBUTION_MAX_MISSING',
  /** Low-means resident with a refundable contribution balance. r 298-10(4) is not priced yet; holds. */
  'ACCOMMODATION_RAC_BALANCE_UNRESOLVED',
] as const;

export type ResidentialFeeReasonCode = (typeof RESIDENTIAL_FEE_REASON_CODES)[number];

const REASON_ORDER = new Map<ResidentialFeeReasonCode, number>(
  RESIDENTIAL_FEE_REASON_CODES.map((code, index) => [code, index]),
);

/** Stable order matches `RESIDENTIAL_FEE_REASON_CODES`, so snapshots do not depend on insertion order. */
export function orderResidentialFeeReasonCodes(
  codes: Iterable<ResidentialFeeReasonCode>,
): ResidentialFeeReasonCode[] {
  const unique = new Set(codes);
  return [...unique].sort((left, right) => REASON_ORDER.get(left)! - REASON_ORDER.get(right)!);
}
